// src/scenes/dashboardFinance/KPIDashboard.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
    CircularProgress,
    Button,
    Grid,
    Card,
    CardContent,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isBetween from "dayjs/plugin/isBetween";
import { ResponsivePie } from "@nivo/pie";
import { ResponsiveBar } from "@nivo/bar";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);

// School‐year helper
function getSchoolYearRange(today = dayjs()) {
    const year = today.month() < 6 ? today.year() - 1 : today.year();
    return {
        start: dayjs(`${year}-07-01`),
        end: dayjs(`${year + 1}-06-30`),
    };
}

// Fetch helper (no credentials) to work with Access-Control-Allow-Origin: *
async function fetchJson(url) {
    try {
        const res = await fetch(url);
        if (!res.ok) return [];
        return await res.json();
    } catch {
        return [];
    }
}

export default function KPIDashboard() {
    const theme = useTheme();
    const textColor = theme.palette.text.primary;

    // raw view data
    const [mplhData, setMplhData] = useState([]);
    const [expMeqData, setExpMeqData] = useState([]);
    const [incMeqData, setIncMeqData] = useState([]);
    const [netMeqData, setNetMeqData] = useState([]);
    const [mixData, setMixData] = useState([]);
    const [totalsData, setTotalsData] = useState([]);
    const [metricData, setMetricData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // filters
    const [site, setSite] = useState("All Sites");
    const { start: schoolStart, end: schoolEnd } = getSchoolYearRange();
    const [monthStart, setMonthStart] = useState(schoolStart);
    const [monthEnd, setMonthEnd] = useState(schoolEnd);

    useEffect(() => {
        (async () => {
            const base = "https://figazuresql.swancreate.com/api/swa";
            const [
                mplhJson,
                expJson,
                incJson,
                netJson,
                mixJson,
                totJson,
                metricJson
            ] = await Promise.all([
                fetchJson(`${base}/vw_KPI_MealsPerLaborHourBySiteMonth`),
                fetchJson(`${base}/vw_KPI_ExpensePerMEQBySiteMonth`),
                fetchJson(`${base}/vw_KPI_IncomePerMEQBySiteMonth`),
                fetchJson(`${base}/vw_KPI_NetPerMEQBySiteMonth`),
                fetchJson(`${base}/vw_KPI_ExpenseMixBySiteMonth`),
                fetchJson(`${base}/vw_FINANCE_KPI_TotalsBySiteMonth`),
                fetchJson(`${base}/vw_KPIMetricsByMonthSiteSubcategory`)
            ]);

            setMplhData(mplhJson);
            setExpMeqData(expJson);
            setIncMeqData(incJson);
            setNetMeqData(netJson);
            setMixData(mixJson);
            setTotalsData(totJson);
            setMetricData(metricJson);
            setLoading(false);
        })().catch(e => {
            setError(e.message);
            setLoading(false);
        });
    }, []);

    const sites = useMemo(() => {
        const all = Array.from(new Set(totalsData.map(r => r.SiteName)))
            .filter(Boolean)
            .sort();
        return ["All Sites", ...all];
    }, [totalsData]);

    const filterFn = r => {
        const d = dayjs(r.YearMonth + "-01");
        return (
            (site === "All Sites" || r.SiteName === site) &&
            d.isBetween(monthStart, monthEnd, "month", "[]")
        );
    };

    const recMPLH = useMemo(
        () => mplhData.find(filterFn) || { MPLH: 0, TotalMEQ: 0, TotalLaborHours: 0 },
        [mplhData, site, monthStart, monthEnd]
    );
    const recEXP = useMemo(
        () => expMeqData.find(filterFn) || { ExpensePerMEQ: 0, TotalExpenses: 0, TotalMEQ: 0 },
        [expMeqData, site, monthStart, monthEnd]
    );
    const recINC = useMemo(
        () => incMeqData.find(filterFn) || { IncomePerMEQ: 0, TotalIncomes: 0, TotalMEQ: 0 },
        [incMeqData, site, monthStart, monthEnd]
    );
    const recNET = useMemo(
        () => netMeqData.find(filterFn) || { NetPerMEQ: 0, TotalIncomes: 0, TotalExpenses: 0, TotalMEQ: 0 },
        [netMeqData, site, monthStart, monthEnd]
    );

    const mixSlice = useMemo(() => mixData.filter(filterFn), [mixData, site, monthStart, monthEnd]);
    const totSlice = useMemo(() => totalsData.filter(filterFn), [totalsData, site, monthStart, monthEnd]);
    const metricSlice = useMemo(() => metricData.filter(filterFn), [metricData, site, monthStart, monthEnd]);

    const pieData = mixSlice.map(d => ({
        id: d.kpi_categoryname,
        label: d.kpi_categoryname,
        value: d.ExpensePct
    }));

    const barData = (() => {
        const byMonth = {};
        mplhData.forEach(r => {
            const d = dayjs(r.YearMonth + "-01");
            if ((site === "All Sites" || r.SiteName === site) &&
                d.isBetween(monthStart, monthEnd, "month", "[]")) {
                byMonth[r.YearMonth] = r.MPLH;
            }
        });
        return Object.entries(byMonth)
            .map(([month, MPLH]) => ({ month, MPLH }))
            .sort((a, b) => a.month.localeCompare(b.month));
    })();

    const downloadTotalsCSV = () => {
        const header = ["Month", "Site", "KPI", "Expenses", "Incomes", "Type"];
        const rows = totSlice.map(r => [
            r.YearMonth, r.SiteName, r.KPI_CategoryName,
            r.TotalExpenses, r.TotalIncomes, r.Type
        ]);
        const csv = [header.join(","), ...rows.map(r => r.join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = "kpi_totals.csv";
        link.click();
    };

    if (loading) {
        return (
            <Box m={4} display="flex" justifyContent="center">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box m={4}>
                <Typography color="error">Error: {error}</Typography>
            </Box>
        );
    }

    return (
        <Box p={4}>
            {/* Filters */}
            <Paper sx={{ p: 3, mb: 4, boxShadow: `0 0 20px ${theme.palette.primary.main}33` }}>
                <Typography variant="h4" color={textColor} mb={2}>
                    KPI Dashboard
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                    <FormControl sx={{ minWidth: 200 }}>
                        <InputLabel sx={{ color: textColor }}>Site</InputLabel>
                        <Select
                            value={site}
                            onChange={e => setSite(e.target.value)}
                            sx={{
                                color: textColor,
                                ".MuiOutlinedInput-notchedOutline": { borderColor: textColor }
                            }}
                        >
                            {sites.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <Box component="label" sx={{ color: textColor }}>
                        From
                        <input
                            type="month"
                            value={monthStart.format("YYYY-MM")}
                            onChange={e => setMonthStart(dayjs(e.target.value + "-01"))}
                            style={{
                                marginLeft: 8, padding: 6,
                                border: `1px solid ${textColor}`,
                                borderRadius: 4,
                                background: theme.palette.background.default,
                                color: textColor
                            }}
                        />
                    </Box>
                    <Box component="label" sx={{ color: textColor }}>
                        To
                        <input
                            type="month"
                            value={monthEnd.format("YYYY-MM")}
                            onChange={e => setMonthEnd(dayjs(e.target.value + "-01"))}
                            style={{
                                marginLeft: 8, padding: 6,
                                border: `1px solid ${textColor}`,
                                borderRadius: 4,
                                background: theme.palette.background.default,
                                color: textColor
                            }}
                        />
                    </Box>
                    <Box flexGrow={1} />
                    <Button variant="contained" color="success" onClick={downloadTotalsCSV}>
                        Download Totals CSV
                    </Button>
                </Box>
            </Paper>

            {/* Summary Cards */}
            <Grid container spacing={3} mb={4}>
                {[
                    { label: "Meals/Labor Hour", ratio: recMPLH.MPLH, detail: `${recMPLH.TotalMEQ.toFixed(2)} MEQ ÷ ${recMPLH.TotalLaborHours.toFixed(2)} hrs`, color: "primary" },
                    { label: "Expense/MEQ", ratio: recEXP.ExpensePerMEQ, detail: `${recEXP.TotalExpenses.toFixed(2)} ÷ ${recEXP.TotalMEQ.toFixed(2)}`, color: "error" },
                    { label: "Income/MEQ", ratio: recINC.IncomePerMEQ, detail: `${recINC.TotalIncomes.toFixed(2)} ÷ ${recINC.TotalMEQ.toFixed(2)}`, color: "success" },
                    { label: "Net/MEQ", ratio: recNET.NetPerMEQ, detail: `${(recNET.TotalIncomes - recNET.TotalExpenses).toFixed(2)} ÷ ${recNET.TotalMEQ.toFixed(2)}`, color: "warning" }
                ].map(({ label, ratio, detail, color }) => {
                    const bg = theme.palette[color].main;
                    const fg = theme.palette.getContrastText(bg);
                    return (
                        <Grid item xs={12} sm={3} key={label}>
                            <Card sx={{ background: bg, color: fg, p: 2, boxShadow: `0 0 12px ${bg}66` }}>
                                <CardContent>
                                    <Typography variant="subtitle2" color={fg}>{label}</Typography>
                                    <Typography variant="h4" color={fg}>{ratio}</Typography>
                                    <Typography variant="caption" color={fg}>{detail}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Charts */}
            <Grid container spacing={3} mb={4}>
                <Grid item xs={12} md={6} sx={{ height: 300 }}>
                    <Paper sx={{ height: "100%", p: 2, boxShadow: `0 0 20px ${theme.palette.primary.main}33` }}>
                        <Typography mb={1} color={textColor}>Expense Mix (%)</Typography>
                        <ResponsivePie
                            data={pieData}
                            margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                            innerRadius={0.5}
                            padAngle={0.7}
                            cornerRadius={3}
                            colors={{ scheme: "category10" }}
                            theme={{
                                textColor,
                                tooltip: { container: { background: theme.palette.background.paper, color: textColor } }
                            }}
                            arcLinkLabelsTextColor={textColor}
                            arcLinkLabelsSkipAngle={10}
                            arcLinkLabelsThickness={2}
                            arcLinkLabelsColor={{ from: "color" }}
                            tooltip={({ datum }) => (
                                <Box sx={{
                                    p: 1,
                                    background: theme.palette.background.paper,
                                    color: textColor,
                                    borderRadius: 1
                                }}>
                                    {datum.id}: {datum.value}%
                                </Box>
                            )}
                        />
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6} sx={{ height: 300 }}>
                    <Paper sx={{ height: "100%", p: 2, boxShadow: `0 0 20px ${theme.palette.secondary.main}33` }}>
                        <Typography mb={1} color={textColor}>MPLH Trend</Typography>
                        <ResponsiveBar
                            data={barData}
                            keys={["MPLH"]}
                            indexBy="month"
                            margin={{ top: 20, right: 20, bottom: 80, left: 80 }}
                            padding={0.3}
                            colors={theme.palette.secondary.main}
                            theme={{
                                axis: { ticks: { text: { fill: textColor } }, legend: { text: { fill: textColor } } },
                                tooltip: { container: { background: theme.palette.background.paper, color: textColor } }
                            }}
                            axisBottom={{ tickRotation: -45 }}
                            enableLabel={false}
                            tooltip={({ indexValue, value }) => (
                                <Box sx={{
                                    p: 1,
                                    background: theme.palette.background.paper,
                                    color: textColor,
                                    borderRadius: 1
                                }}>
                                    {indexValue}: {value}
                                </Box>
                            )}
                        />
                    </Paper>
                </Grid>
            </Grid>

            {/* Detail Grid */}
            <Paper sx={{ height: 500, p: 2, boxShadow: `0 0 20px ${theme.palette.grey[500]}33` }}>
                <Typography mb={2} color={textColor}>KPI Metrics by Subcategory</Typography>
                <DataGrid
                    rows={metricSlice.map((r, i) => ({
                        id: i,
                        Month: r.YearMonth,
                        KPI: r.kpi_categoryname,
                        Subcat: r.SubcategoryName,
                        Expense: r.TotalExpense,
                        Income: r.TotalIncome,
                        Net: r.NetKPIValue
                    }))}
                    columns={[
                        { field: "Month", headerName: "Month", flex: 1 },
                        { field: "KPI", headerName: "KPI", flex: 1.2 },
                        { field: "Subcat", headerName: "Subcategory", flex: 1.5 },
                        {
                            field: "Expense",
                            headerName: "Expenses",
                            flex: 1,
                            valueFormatter: ({ value }) =>
                                Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" })
                        },
                        {
                            field: "Income",
                            headerName: "Incomes",
                            flex: 1,
                            valueFormatter: ({ value }) =>
                                Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" })
                        },
                        {
                            field: "Net",
                            headerName: "Net",
                            flex: 1,
                            valueFormatter: ({ value }) =>
                                Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" })
                        }
                    ]}
                    pageSize={10}
                    rowsPerPageOptions={[10, 25, 50]}
                    sx={{
                        ".MuiDataGrid-row:hover": { backgroundColor: theme.palette.action.hover },
                        ".MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.background.paper }
                    }}
                />
            </Paper>
        </Box>
    );
}
